import React, { memo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaBell,
  FaSignOutAlt,
  FaUser,
  FaCog,
  FaShoppingCart,
  FaChartLine,
  FaStore
} from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspaceShell } from '../../../contexts/WorkspaceShellContext';
import useWorkspaceEmpresa from '../../../hooks/useWorkspaceEmpresa';
import useWorkspaceQuickAccess from '../../../hooks/useWorkspaceQuickAccess';
import BrandLogo from '../../common/BrandLogo';
import AppVersionDisplay from '../../common/AppVersionDisplay';
import UserAvatar from '../../common/UserAvatar';
import { getUserFullName } from '../../../utils/userIdentity';
import CommandPalettePlaceholder from './CommandPalettePlaceholder';

/**
 * Header compacto — identidad del negocio + accesos rápidos.
 */
const WorkspaceHeader = memo(function WorkspaceHeader({
  isPhone = false,
  onOpenMobileMenu
}) {
  const { currentUser, logout, sucursalSeleccionada, sucursalesDisponibles, cambiarSucursal, loadingSucursales } = useAuth();
  const { openCommandPalette, focusMode } = useWorkspaceShell();
  const { nombreEmpresa, loading: loadingEmpresa } = useWorkspaceEmpresa();
  const { canPuntoVenta, canReportes, canStock } = useWorkspaceQuickAccess();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openCommandPalette();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openCommandPalette]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  if (focusMode) return null;

  const sucursalNombre = sucursalSeleccionada?.nombre
    || (sucursalesDisponibles.length === 1 ? sucursalesDisponibles[0]?.nombre : null);
  const mostrarSelectorSucursal = sucursalesDisponibles.length > 1;

  const renderSelectorSucursal = (className) => (
    <select
      value={sucursalSeleccionada?.id || ''}
      onChange={(e) => cambiarSucursal(e.target.value)}
      className={className}
      disabled={loadingSucursales}
      aria-label="Seleccionar sucursal"
    >
      <option value="">
        {loadingSucursales ? 'Cargando…' : 'Sucursal…'}
      </option>
      {sucursalesDisponibles.map((sucursal) => (
        <option key={sucursal.id} value={sucursal.id}>
          {sucursal.nombre}
        </option>
      ))}
    </select>
  );

  return (
    <header className="e0e3-workspace-header relative z-40 shrink-0 border-b border-white/[0.06] bg-e0e3-shell/95 backdrop-blur-md">
      <div className="flex min-h-12 items-center gap-2 px-3 py-1.5 sm:px-4">
        {isPhone && (
          <button
            type="button"
            onClick={onOpenMobileMenu}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-e0e3-muted hover:bg-white/[0.08] hover:text-white lg:hidden"
            aria-label="Abrir menú"
          >
            <FaBars size={18} />
          </button>
        )}

        {isPhone ? (
          <Link to="/" className="flex shrink-0 items-center">
            <BrandLogo className="h-7 w-7" showText={false} variant="mark" />
          </Link>
        ) : (
          <div className="hidden min-w-0 shrink-0 sm:block sm:max-w-[220px] md:max-w-[280px]">
            <Link to="/" className="block min-w-0">
              <p className="truncate text-sm font-bold leading-tight text-white md:text-base">
                {loadingEmpresa ? (
                  <span className="inline-block h-4 w-28 animate-pulse rounded bg-white/10" />
                ) : (
                  nombreEmpresa
                )}
              </p>
              {sucursalNombre && (
                <p className="mt-0.5 truncate text-[11px] text-e0e3-muted">{sucursalNombre}</p>
              )}
            </Link>
          </div>
        )}

        <div className="hidden min-w-0 flex-1 justify-center lg:flex">
          <CommandPalettePlaceholder onClick={openCommandPalette} />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
          {canPuntoVenta && (
            <Link
              to="/punto-venta"
              className="e0e3-header-pv hidden items-center gap-1.5 rounded-lg bg-e0e3-accent px-2.5 py-1.5 text-xs font-semibold text-white shadow-md shadow-e0e3-accent/25 transition hover:bg-e0e3-accent/90 sm:inline-flex md:px-3 md:text-sm"
            >
              <FaShoppingCart size={14} aria-hidden />
              <span className="hidden md:inline">Punto de Venta</span>
              <span className="md:hidden">PV</span>
            </Link>
          )}

          {canReportes && (
            <Link
              to="/reportes"
              className="hidden h-9 w-9 items-center justify-center rounded-lg text-e0e3-muted transition hover:bg-white/[0.08] hover:text-e0e3-foreground sm:flex"
              title="Reportes"
              aria-label="Reportes"
            >
              <FaChartLine size={16} />
            </Link>
          )}

          {canStock && (
            <Link
              to="/productos?filtro=stock_bajo"
              className="hidden h-9 w-9 items-center justify-center rounded-lg text-e0e3-muted transition hover:bg-white/[0.08] hover:text-e0e3-foreground sm:flex"
              title="Stock bajo"
              aria-label="Alertas de stock"
            >
              <FaBell size={16} />
            </Link>
          )}

          {mostrarSelectorSucursal && (
            <div className="hidden items-center sm:flex">
              <FaStore className="mr-1.5 shrink-0 text-e0e3-muted" size={12} aria-hidden />
              {renderSelectorSucursal('nexo-field-header max-w-[140px] py-1.5 text-xs xl:max-w-[180px]')}
            </div>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 rounded-lg px-1 py-1 transition hover:bg-white/[0.06]"
              aria-expanded={showUserMenu}
            >
              <span className="hidden min-w-0 text-right md:block">
                <span className="block max-w-[120px] truncate text-xs font-semibold text-white lg:max-w-[160px]">
                  {getUserFullName(currentUser)}
                </span>
              </span>
              <UserAvatar user={currentUser} size="sm" />
            </button>
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-[79]" onClick={() => setShowUserMenu(false)} aria-hidden />
                <div className="absolute right-0 z-[80] mt-1 w-56 overflow-hidden rounded-xl border border-white/[0.08] bg-e0e3-shell py-1 shadow-xl">
                  <div className="border-b border-white/[0.08] px-3 py-2">
                    <UserAvatar user={currentUser} size="sm" showMeta />
                  </div>
                  <Link to="/perfil" className="flex items-center gap-2 px-3 py-2 text-sm text-e0e3-foreground hover:bg-white/[0.08]" onClick={() => setShowUserMenu(false)}>
                    <FaUser size={14} /> Mi perfil
                  </Link>
                  <Link to="/configuracion/empresa" className="flex items-center gap-2 px-3 py-2 text-sm text-e0e3-foreground hover:bg-white/[0.08]" onClick={() => setShowUserMenu(false)}>
                    <FaCog size={14} /> Configuración
                  </Link>
                  <button type="button" onClick={handleLogout} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-e0e3-foreground hover:bg-white/[0.08]">
                    <FaSignOutAlt size={14} /> Cerrar sesión
                  </button>
                  <AppVersionDisplay variant="menu" poll />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isPhone && (
        <div className="border-t border-white/[0.06] px-3 py-1.5 sm:hidden">
          {mostrarSelectorSucursal && (
            <div className="mb-1.5 flex items-center">
              <FaStore className="mr-1.5 shrink-0 text-e0e3-muted" size={12} aria-hidden />
              {renderSelectorSucursal('nexo-field-header w-full py-1.5 text-xs')}
            </div>
          )}
          <CommandPalettePlaceholder onClick={openCommandPalette} className="h-8 max-w-none text-xs" />
          {canPuntoVenta && (
            <Link
              to="/punto-venta"
              className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-lg bg-e0e3-accent py-2 text-sm font-semibold text-white"
            >
              <FaShoppingCart size={14} /> Punto de Venta
            </Link>
          )}
        </div>
      )}
    </header>
  );
});

export default WorkspaceHeader;
